import { useMemo, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded'
import LockIcon from '@mui/icons-material/Lock'
import TagIcon from '@mui/icons-material/Tag'
import { useChat } from '../../context/useChat'
import { useChatControls } from '../../hooks/useChatControls'
import { useOptionalChatSelection } from '../../context/chatSelectionContextCore'
import { useNls } from '../../nls/nlsProvider'
import { OverflowTip } from '../Common/Hover'
import { CreateChannelModal } from './CreateChannelModal'
import { Button } from '../Common/Button'
import { Pressable } from '../Common/Pressable'
import { RailChipTabs, CountBadge, StatusChip } from '../Common'
import { maskWhatsappNumber } from '../../utils/whatsappChat'
import { useRailPageShellCollapse } from '../Common/railPageShellContext'
import { buildChannelToken, buildTagToken } from '../../types/aiChatSource'

const firstInitial = (s: string): string => {
  const trimmed = s.trim().replace(/^#+/, '')
  if (!trimmed) return '·'
  return trimmed.charAt(0).toUpperCase()
}

const HASHTAG_PATTERN = /(^|\s)#([A-Za-z][A-Za-z0-9_-]*)/g

const extractHashtags = (text: string): string[] => {
  const tags = new Set<string>()
  for (const match of text.matchAll(HASHTAG_PATTERN)) tags.add(match[2].toLowerCase())
  return Array.from(tags)
}

type RailTab = 'CHANNELS' | 'TAGS'

/**
 * Left rail for the 3-column Chat page. Visually mirrors the AI Magic history
 * panel (`aiChatHistory*` classes) — pill rows, "New" style header button, and
 * a segmented Channels/Tags switcher at the top. Semantics for both lists are
 * preserved from the legacy `ChannelSidebar` + `TeamGroupChat` sidebar; only
 * the chrome is redesigned.
 */
export const ChatChannelRail = () => {
  const {
    channelsList,
    messagesStream,
    activeChannelId,
    unreadByChannel,
    activeTagFilter,
    setActiveChannelId,
    setActiveTagFilter,
    markRead,
  } = useChat()
  const chatSelection = useOptionalChatSelection()
  const { t } = useNls()

  const [tab, setTab] = useState<RailTab>('CHANNELS')
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const { collapsed: isRailCollapsed } = useRailPageShellCollapse()
  const { canCreateChannel } = useChatControls()

  const activeChannels = useMemo(
    () => channelsList.filter((channel) => !channel.archivedAt && !channel.permanentlyDeletedAt),
    [channelsList],
  )

  const generalChannels = useMemo(
    () => activeChannels.filter((channel) => channel.identityType !== 'WHATSAPP'),
    [activeChannels],
  )

  const whatsappChannels = useMemo(
    () => activeChannels.filter((channel) => channel.identityType === 'WHATSAPP'),
    [activeChannels],
  )

  const tags = useMemo(() => {
    const map = new Map<string, { name: string; count: number }>()
    for (const message of messagesStream) {
      for (const tag of extractHashtags(message.textContent)) {
        const existing = map.get(tag)
        map.set(tag, { name: tag, count: (existing?.count ?? 0) + 1 })
      }
    }
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name))
  }, [messagesStream])

  const handleSelectChannel = (channelId: string) => {
    if (chatSelection?.mode === 'select') {
      const channel = channelsList.find((candidate) => candidate.id === channelId)
      if (channel) chatSelection.toggleToken(buildChannelToken(channel))
      return
    }
    setActiveChannelId(channelId)
    void markRead(channelId)
  }

  return (
    <aside className={`chatPage3C__rail${isRailCollapsed ? ' chatPage3C__rail--collapsed' : ''}`} aria-label={t('NLSChatLabelChatsDiscussions')}>
      {isRailCollapsed && (
        <RailChipTabs<'CHANNELS' | 'TAGS'>
          items={[
            {
              key: 'CHANNELS',
              label: t('NLSChatLabelChannels'),
              icon: <ChatBubbleOutlineRoundedIcon style={{ fontSize: 15 }} />,
            },
            {
              key: 'TAGS',
              label: t('NLSChatLabelTags'),
              icon: <TagIcon style={{ fontSize: 15 }} />,
            },
          ]}
          value={tab}
          onChange={setTab}
          ariaLabel={t('NLSChatLabelChatsDiscussions')}
          orientation="vertical"
          className="chatPage3C__miniSwitcher"
        />
      )}
      <RailChipTabs<'CHANNELS' | 'TAGS'>
        items={[
          { key: 'CHANNELS', label: t('NLSChatLabelChannels') },
          { key: 'TAGS', label: t('NLSChatLabelTags') },
        ]}
        value={tab}
        onChange={setTab}
        ariaLabel={t('NLSChatLabelChatsDiscussions')}
        orientation="horizontal"
        className="chatPage3C__tabs"
      />

      {tab === 'CHANNELS' && canCreateChannel && (
        <Button
          variant="tertiary"
          size="lg"
          className="aiChatNewButton chatPage3C__newButton"
          onClick={() => setIsCreateOpen(true)}
          title={t('NLSChatActionCreateChannel')}
          icon={<AddRoundedIcon fontSize="inherit" />}
        >
          {t('NLSChatActionCreateChannel')}
        </Button>
      )}

      <div className="aiChatHistoryList chatPage3C__list">
        {tab === 'CHANNELS' ? (
          <AnimatePresence initial={false}>
            {generalChannels.map((channel) => {
              const isActive = channel.id === activeChannelId
              const hasUnread = Boolean(unreadByChannel[channel.id])
              const label = channel.slug || channel.name
              return (
                <motion.div
                  key={channel.id}
                  layout
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.14 }}
                  className={`aiChatHistoryRow${isActive ? ' isActive' : ''}`}
                >
                  <Pressable
                    className="aiChatHistoryOpen"
                    onClick={() => handleSelectChannel(channel.id)}
                    aria-current={isActive ? 'true' : undefined}
                    aria-label={isRailCollapsed ? label : undefined}
                    title={isRailCollapsed ? label : undefined}
                  >
                    <span className="chatPage3C__pillIcon" aria-hidden="true">
                      {isRailCollapsed
                        ? firstInitial(label)
                        : channel.type === 'private' ? <LockIcon style={{ fontSize: 13 }} /> : '#'}
                    </span>
                    <OverflowTip label={label} placement="right-start" group="chatChannelRail" offset={14}>
                      <span className="aiChatHistoryOpenLabel">{label}</span>
                    </OverflowTip>
                    {hasUnread && !isActive && (
                      <span className="chatPage3C__unreadDot" aria-label={t('NLSChatLabelUnread')} />
                    )}
                  </Pressable>
                </motion.div>
              )
            })}
            {generalChannels.length === 0 && (
              <p className="chatPage3C__empty">{t('NLSChatEmptyNoChannelsCreateOne')}</p>
            )}

            {whatsappChannels.length > 0 && (
              <div className="chatPage3C__whatsappSection" data-testid="chat-whatsapp-section">
                <div className="chatWhatsAppSectionHeader">
                  <span>{t('NLSChatSectionWhatsApp')}</span>
                  <CountBadge value={whatsappChannels.length} />
                </div>
                {whatsappChannels.map((channel) => {
                  const isActive = channel.id === activeChannelId
                  const hasUnread = Boolean(unreadByChannel[channel.id])
                  const label = channel.name || channel.slug
                  const maskedPhone = maskWhatsappNumber(channel.whatsappE164)
                  return (
                    <motion.div
                      key={channel.id}
                      layout
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      transition={{ duration: 0.14 }}
                      className={`aiChatHistoryRow${isActive ? ' isActive' : ''}`}
                    >
                      <Pressable
                        className="aiChatHistoryOpen"
                        onClick={() => handleSelectChannel(channel.id)}
                        aria-current={isActive ? 'true' : undefined}
                        aria-label={isRailCollapsed ? label : undefined}
                        title={isRailCollapsed ? label : undefined}
                      >
                        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <StatusChip tone="success" size="sm">
                              WhatsApp
                            </StatusChip>
                            <OverflowTip label={label} placement="right-start" group="chatChannelRail" offset={14}>
                              <span className="aiChatHistoryOpenLabel">{label}</span>
                            </OverflowTip>
                          </div>
                          {maskedPhone ? (
                            <span className="chatWhatsAppMaskedPhone">
                              {maskedPhone}
                            </span>
                          ) : null}
                        </div>
                        {hasUnread && !isActive && (
                          <span className="chatPage3C__unreadDot" aria-label={t('NLSChatLabelUnread')} />
                        )}
                      </Pressable>
                    </motion.div>
                  )
                })}
              </div>
            )}
          </AnimatePresence>
        ) : (
          <>
            <div className={`aiChatHistoryRow${activeTagFilter === null ? ' isActive' : ''}`}>
              <Pressable
                className="aiChatHistoryOpen"
                onClick={() => setActiveTagFilter(null)}
                aria-current={activeTagFilter === null ? 'true' : undefined}
                aria-label={isRailCollapsed ? t('NLSChatLabelAllChats') : undefined}
                title={isRailCollapsed ? t('NLSChatLabelAllChats') : undefined}
              >
                <span className="chatPage3C__pillIcon" aria-hidden="true">
                  {isRailCollapsed ? firstInitial(t('NLSChatLabelAllChats')) : <TagIcon style={{ fontSize: 13 }} />}
                </span>
                <span className="aiChatHistoryOpenLabel">{t('NLSChatLabelAllChats')}</span>
                <span className="chatPage3C__pillCount">{messagesStream.length}</span>
              </Pressable>
            </div>
            {tags.map((tag) => {
              const isActive = activeTagFilter === tag.name
              return (
                <div key={tag.name} className={`aiChatHistoryRow${isActive ? ' isActive' : ''}`}>
                  <Pressable
                    className="aiChatHistoryOpen"
                    onClick={() => {
                      if (chatSelection?.mode === 'select') {
                        chatSelection.toggleToken(buildTagToken(tag.name, activeChannelId ?? undefined))
                        return
                      }
                      setActiveTagFilter(isActive ? null : tag.name)
                    }}
                    aria-current={isActive ? 'true' : undefined}
                    aria-label={isRailCollapsed ? `#${tag.name}` : undefined}
                    title={isRailCollapsed ? `#${tag.name}` : undefined}
                  >
                    <span className="chatPage3C__pillIcon" aria-hidden="true">
                      {isRailCollapsed ? firstInitial(tag.name) : <TagIcon style={{ fontSize: 13 }} />}
                    </span>
                    <OverflowTip label={`#${tag.name}`} placement="right-start" group="chatChannelRail" offset={14}>
                      <span className="aiChatHistoryOpenLabel">#{tag.name}</span>
                    </OverflowTip>
                    <span className="chatPage3C__pillCount">{tag.count}</span>
                  </Pressable>
                </div>
              )
            })}
            {tags.length === 0 && (
              <p className="chatPage3C__empty">{t('NLSChatEmptyNoHashtags')}</p>
            )}
          </>
        )}
      </div>

      <AnimatePresence>
        {isCreateOpen && (
          <CreateChannelModal
            isOpen={isCreateOpen}
            onClose={() => setIsCreateOpen(false)}
            onCreated={(channelId) => setActiveChannelId(channelId)}
          />
        )}
      </AnimatePresence>
    </aside>
  )
}
